import React, { useEffect, useMemo, useState } from "react";
import useReport from "../../hooks/useReport";
import IndicatorInput from "../../components/IndicatorInput";
import SubmitSection from "../../components/SubmitSection";
import useReportsList from "../../hooks/useReportsList";
import { getLang } from "../../store/uiStore";
import { getUser } from "../../store/authStore";
import {
  acceptReport,
  returnReport,
  allowEditReport,
} from "../../api/reports";
import { getSelectedClinic } from "../../store/selectedClinicStore";
import { useNavigate, useParams } from "react-router-dom";

import "./ReportsPage.css";

const getStatusClass = (status) => {
  switch (status) {
    case "accepted":
      return "accepted";
    case "returned":
      return "returned";
    case "submitted":
      return "submitted";
    case "in_progress":
      return "in-progress";
    case "late":
      return "late";
    default:
      return "default";
  }
};

export default function ReportPage() {
  const { reports } = useReportsList();
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [returnReason, setReturnReason] = useState("");

  const user = getUser();
  const isInspector = [
    "regional_inspector",
    "clinic_inspector",
    "central",
  ].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const navigate = useNavigate();
  const { reportId } = useParams();
  const clinic = getSelectedClinic();
  const lang = getLang();

  const { report, loading } = useReport(
    selectedPeriod?.year,
    selectedPeriod?.quarter,
    reportId
  );

  const tr = {
    pageTitle: lang === "ru" ? "Отчёты" : "Ҳисоботҳо",
    clinic: lang === "ru" ? "Учреждение" : "Муассиса",
    reportContext:
      lang === "ru" ? "Просмотр отчёта учреждения" : "Намоиши ҳисоботи муассиса",
    signed: lang === "ru" ? "Подписан" : "Имзо кардааст",
    date: lang === "ru" ? "Дата" : "Сана",
    reportReturned:
      lang === "ru" ? "Отчёт возвращён" : "Ҳисобот баргардонида шуд",
    reason: lang === "ru" ? "Причина" : "Сабаб",
    report: lang === "ru" ? "Отчёт" : "Ҳисобот",
    currentPeriod: lang === "ru" ? "Текущий период" : "Давраи ҷорӣ",
    number: lang === "ru" ? "№" : "№",
    indicator: lang === "ru" ? "Показатель" : "Нишондиҳанда",
    value: lang === "ru" ? "Значение" : "Шумора (миқдор)",
    reportAlreadySent:
      lang === "ru" ? "Отчёт уже отправлен" : "Ҳисобот аллакай фиристода шудааст",
    reportLocked:
      lang === "ru" ? "Отчёт заблокирован" : "Ҳисобот баста шудааст",
    loading: lang === "ru" ? "Загрузка..." : "Боргирӣ...",
    noData: lang === "ru" ? "Нет данных" : "Маълумот нест",
    statusLabel: lang === "ru" ? "Статус" : "Ҳолат",
    quarter: lang === "ru" ? "Период" : "Давра",
    status: {
      draft: lang === "ru" ? "Черновик" : "Лоиҳа",
      in_progress: lang === "ru" ? "В работе" : "Дар коркард",
      submitted: lang === "ru" ? "Отправлен" : "Фиристода шуд",
      accepted: lang === "ru" ? "Принят" : "Қабул шуд",
      returned: lang === "ru" ? "Возвращён" : "Бозгардонида шуд",
      late: lang === "ru" ? "Просрочен" : "Мӯҳлатгузашта",
    },
    review: lang === "ru" ? "Проверка отчёта" : "Санҷиши ҳисобот",
    accept: lang === "ru" ? "Принять отчёт" : "Қабул кардани ҳисобот",
    return: lang === "ru" ? "Вернуть на доработку" : "Барои ислоҳ баргардондан",
    returnPlaceholder:
      lang === "ru" ? "Укажите причину возврата..." : "Сабаби баргардониданро нависед...",
    allowEdit:
      lang === "ru"
        ? "Разрешить повторное редактирование"
        : "Иҷозат додани таҳрири дубора",
    editAllowed:
      lang === "ru" ? "Редактирование разрешено" : "Таҳрир иҷозат дода шудааст",
    until: lang === "ru" ? "До" : "То",
    confirmAllow:
      lang === "ru"
        ? "Разрешить учреждению повторное редактирование отчёта?"
        : "Ба муассиса иҷозати таҳрири дубораи ҳисобот дода шавад?",
    allowedMessage:
      lang === "ru" ? "Редактирование разрешено." : "Таҳрир иҷозат дода шуд.",
    errorAllow:
      lang === "ru"
        ? "Не удалось разрешить редактирование."
        : "Иҷозати таҳрир додан муяссар нашуд.",
  };

  const getQuarterName = (quarter) => {
    const namesTj = {
      1: "Семоҳа",
      2: "Шашмоҳа",
      3: "Нуҳмоҳа",
      4: "Солона",
    };
    const namesRu = {
      1: "Квартальный",
      2: "Полугодовой",
      3: "Девятимесячный",
      4: "Годовой",
    };
    return lang === "ru" ? namesRu[quarter] : namesTj[quarter];
  };

  const clinicName = useMemo(() => {
    if (clinic?.name) return clinic.name;
    if (report?.clinic_name) return report.clinic_name;
    if (report?.clinic?.name) return report.clinic.name;
    return lang === "ru" ? "Учреждение" : "Муассиса";
  }, [clinic, report, lang]);

  useEffect(() => {
    const inspectorRoles = [
      "regional_inspector",
      "clinic_inspector",
      "central",
    ];

    if (inspectorRoles.includes(user?.role)) {
      if (!clinic && !reportId) {
        navigate("/clinics");
        return;
      }

      if (
        !reportId &&
        !["submitted", "returned", "accepted"].includes(
          clinic?.latest_report_status
        )
      ) {
        navigate("/clinics");
      }
    }
  }, [clinic, navigate, user, reportId]);

  const handleAccept = async () => {
    try {
      await acceptReport(report.id);
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturn = async () => {
    if (!returnReason.trim()) return;

    try {
      await returnReport(report.id, returnReason);
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handleAllowEdit = async () => {
    if (!window.confirm(tr.confirmAllow)) return;

    try {
      await allowEditReport(report.id);
      alert(tr.allowedMessage);
      window.location.reload();
    } catch (error) {
      console.error(error);
      alert(error?.response?.data?.detail || tr.errorAllow);
    }
  };

  if (loading) {
    return (
      <main className="nizom-reports-page">
        <div className="nizom-reports-container">
          <div className="nizom-reports-state">{tr.loading}</div>
        </div>
      </main>
    );
  }

  if (!report) {
    return (
      <main className="nizom-reports-page">
        <div className="nizom-reports-container">
          <div className="nizom-reports-state">{tr.noData}</div>
        </div>
      </main>
    );
  }

  return (
    <main className="nizom-reports-page">
      <div className="nizom-reports-container">
        <header className="nizom-reports-header">
          <div>
            <div className="nizom-reports-eyebrow">{tr.reportContext}</div>
            <h1>{tr.pageTitle}</h1>
          </div>
        </header>

        <section className="nizom-report-clinic-card">
          <div className="nizom-report-clinic-label">{tr.clinic}</div>
          <div className="nizom-report-clinic-name">{clinicName}</div>
          <div className="nizom-report-period">
            {tr.report} {report.year} · {getQuarterName(report.quarter)}
          </div>
        </section>

        <section className="nizom-report-meta-card">
          <div className="nizom-report-meta-item">
            <span>{tr.statusLabel}</span>
            <span className={`nizom-report-status ${getStatusClass(report.actual_status)}`}>
              <span className="nizom-report-status-dot" />
              {tr.status[report.actual_status] || report.actual_status}
            </span>
          </div>

          {report.signed && (
            <div className="nizom-report-meta-item">
              <span>{tr.signed}</span>
              <strong>
                {report.signed_by_name}
                {report.signed_by_position
                  ? ` (${report.signed_by_position})`
                  : ""}
              </strong>
            </div>
          )}

          {report.signed && report.signed_at && (
            <div className="nizom-report-meta-item">
              <span>{tr.date}</span>
              <strong>
                {new Date(report.signed_at).toLocaleString(
                  lang === "ru" ? "ru-RU" : "tg-TJ"
                )}
              </strong>
            </div>
          )}
        </section>

        {!isInspector &&
          (report.signed || report.is_locked) && (
            <div className="nizom-report-notice info-notice">
              {report.signed
                ? tr.reportAlreadySent
                : tr.reportLocked}
            </div>
          )}

        {report.actual_status === "returned" && report.return_reason && (
          <div className="nizom-report-notice returned-notice">
            <strong>{tr.reportReturned}</strong>
            <div>
              {tr.reason}: {report.return_reason}
            </div>
          </div>
        )}

        {isAdmin && report.actual_status === "late" && (
          <div className="nizom-report-admin-card">
            <div>
              <strong>{tr.status.late}</strong>
            </div>
            <button
              type="button"
              className="report-btn warning"
              onClick={handleAllowEdit}
            >
              {tr.allowEdit}
            </button>
          </div>
        )}

        {isAdmin &&
          report.edit_allowed_by_admin &&
          report.edit_allowed_until && (
            <div className="nizom-report-notice edit-notice">
              <strong>{tr.editAllowed}</strong>
              <div>
                {tr.until}:{" "}
                {new Date(report.edit_allowed_until).toLocaleString(
                  lang === "ru" ? "ru-RU" : "tg-TJ"
                )}
              </div>
            </div>
          )}

        {isInspector && report.actual_status !== "accepted" && (
          <section className="nizom-report-review-card">
            <div className="nizom-report-review-title">{tr.review}</div>

            <div className="nizom-report-review-actions">
              <button
                type="button"
                className="report-btn success"
                onClick={handleAccept}
              >
                {tr.accept}
              </button>
            </div>

            <div className="nizom-report-return">
              <textarea
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                rows={3}
                placeholder={tr.returnPlaceholder}
              />
              <button
                type="button"
                className="report-btn danger"
                onClick={handleReturn}
                disabled={!returnReason.trim()}
              >
                {tr.return}
              </button>
            </div>
          </section>
        )}

        <section className="nizom-report-period-card">
          <div>
            <span>{tr.quarter}</span>
            <strong>
              {report.year} — {getQuarterName(report.quarter)}
            </strong>
          </div>

          <select
            value={
              selectedPeriod
                ? `${selectedPeriod.year}-${selectedPeriod.quarter}`
                : ""
            }
            onChange={(e) => {
              if (!e.target.value) {
                setSelectedPeriod(null);
                return;
              }

              const [year, quarter] = e.target.value.split("-");
              setSelectedPeriod({ year, quarter });
            }}
          >
            <option value="">{tr.currentPeriod}</option>
            {reports.map((r) => (
              <option key={r.id} value={`${r.year}-${r.quarter}`}>
                {r.year} — {getQuarterName(r.quarter)}
              </option>
            ))}
          </select>
        </section>

        <section className="nizom-report-values-card">
          <div className="nizom-report-values-header">
            <div>
              <div className="nizom-report-values-title">
                {tr.report} {report.year} — {getQuarterName(report.quarter)}
              </div>
              <div className="nizom-report-values-subtitle">
                {clinicName}
              </div>
            </div>
          </div>

          <div className="nizom-report-table-wrap">
            <table className="nizom-report-table">
              <thead>
                <tr>
                  <th>{tr.number}</th>
                  <th>{tr.indicator}</th>
                  <th>{tr.value}</th>
                </tr>
              </thead>

              <tbody>
                {report.values.map((item, index) => (
                  <tr key={item.id}>
                    <td className="report-number">{index + 1}</td>
                    <td className="report-indicator">
                      {lang === "ru"
                        ? item.indicator_name_ru || item.indicator_name_tj
                        : item.indicator_name_tj || item.indicator_name_ru}
                    </td>
                    <td className="report-value">
                      <IndicatorInput
                        item={item}
                        canEdit={report.can_edit}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="nizom-report-mobile-list">
            {report.values.map((item, index) => (
              <article className="nizom-report-mobile-item" key={item.id}>
                <div className="nizom-report-mobile-number">
                  {index + 1}
                </div>
                <div className="nizom-report-mobile-content">
                  <div className="nizom-report-mobile-indicator">
                    {lang === "ru"
                        ? item.indicator_name_ru || item.indicator_name_tj
                        : item.indicator_name_tj || item.indicator_name_ru}
                  </div>
                  <div className="nizom-report-mobile-value">
                    <span>{tr.value}</span>
                    <IndicatorInput
                      item={item}
                      canEdit={report.can_edit}
                    />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {report.can_edit && (
          <div className="nizom-report-submit">
            <SubmitSection report={report} />
          </div>
        )}
      </div>
    </main>
  );
}
